import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Check, Files, Loader2, Pencil, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { adminApi, formatApiError } from "@/context/AuthContext";
import { formatEventDate, formatTime } from "@/lib/api";
import { EventForm } from "@/components/admin/EventForm";

const MAX_LINKS = 20;
const PARALLEL = 2;

const parseLinks = (text) => [...new Set(text.split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^https?:\/\//i.test(s)))].slice(0, MAX_LINKS);

const toPayload = (d) => ({
  title: d.title, date: d.date, venue: d.venue, address: d.address || null, city: d.city, image: d.image || null,
  ticket_url: d.ticket_url || d.source_url || null, excerpt: d.excerpt || null, content: d.content || [], embeds: [],
  place_category: d.place_category || "salles", types: d.types || [],
});
const complete = (d) => !!(d?.title && d?.date && d?.venue && d?.city);

const STATUS = {
  loading: { label: "Lecture…", cls: "text-zinc-300" },
  ready: { label: "Prêt", cls: "text-emerald-400" },
  incomplete: { label: "À compléter", cls: "text-yellow-400" },
  duplicate: { label: "Déjà dans l'agenda", cls: "text-yellow-400" },
  error: { label: "Erreur", cls: "text-red-400" },
  saved: { label: "Enregistré", cls: "text-emerald-400" },
};

const statusOf = (d) => (d.duplicate ? "duplicate" : complete(d) ? "ready" : "incomplete");

const Row = ({ row, i, onEdit, onToggle }) => {
  const s = STATUS[row.status];
  const d = row.data;
  return (
    <li className="flex items-start gap-3 py-4 border-b border-white/10" data-testid={`bulk-row-${i}`}>
      <input type="checkbox" checked={row.selected} disabled={!["ready", "duplicate"].includes(row.status)} onChange={onToggle}
        aria-label="Sélectionner" data-testid={`bulk-row-select-${i}`} className="mt-1.5 w-4 h-4 accent-[hsl(var(--primary))]" />
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold truncate">{d?.title || row.url}</p>
        <p className="text-zinc-400 text-sm truncate">
          {d?.date ? `${formatEventDate(d.date)} · ${formatTime(d.date)}` : "Date inconnue"}{d?.venue ? ` — ${d.venue}` : ""}{d?.city ? `, ${d.city}` : ""}
        </p>
        <p className={`text-xs uppercase tracking-[0.15em] font-bold mt-1 ${s.cls}`} data-testid={`bulk-row-status-${i}`}>
          {row.status === "loading" && <Loader2 className="w-3 h-3 inline animate-spin mr-1" />}
          {s.label}{row.status === "duplicate" && d?.duplicate ? ` : « ${d.duplicate.title} »` : ""}{row.error ? ` : ${row.error}` : ""}
        </p>
      </div>
      {d && row.status !== "saved" && (
        <button type="button" onClick={onEdit} aria-label="Vérifier / modifier" data-testid={`bulk-row-edit-${i}`}
          className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-white transition-colors"><Pencil className="w-4 h-4" /></button>
      )}
    </li>
  );
};

export const BulkImport = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const patch = (i, p) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...p } : r)));

  const run = async () => {
    const links = parseLinks(text);
    if (!links.length) { toast.error("Colle au moins un lien complet (https://…)."); return; }
    setBusy(true);
    setRows(links.map((url) => ({ url, status: "loading", selected: false })));
    let next = 0;
    const worker = async () => {
      while (next < links.length) {
        const i = next++;
        try {
          const { data } = await adminApi.post("/admin/events/import", { url: links[i] });
          const status = statusOf(data);
          patch(i, { data, status, selected: status === "ready" });
        } catch (e) {
          patch(i, { status: "error", error: formatApiError(e.response?.data?.detail) });
        }
      }
    };
    await Promise.all(Array.from({ length: PARALLEL }, worker));
    setBusy(false);
  };

  const save = async (i, payload) => {
    await adminApi.post("/admin/events", payload);
    patch(i, { status: "saved", selected: false });
  };

  const saveSelected = async () => {
    setBusy(true);
    let ok = 0;
    for (const [i, r] of rows.entries()) {
      if (!r.selected) continue;
      try { await save(i, toPayload(r.data)); ok += 1; } catch (e) { patch(i, { status: "error", error: formatApiError(e.response?.data?.detail), selected: false }); }
    }
    qc.invalidateQueries({ queryKey: ["/events"] });
    setBusy(false);
    toast.success(`${ok} concert(s) ajouté(s) à l'agenda.`);
  };

  const saveEdited = async (payload) => {
    try {
      await save(editing, payload);
      qc.invalidateQueries({ queryKey: ["/events"] });
      toast.success("Concert enregistré.");
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const selected = rows.filter((r) => r.selected).length;
  const reset = () => { setRows([]); setText(""); };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="bulk-import-open"
        className="inline-flex items-center gap-2 border border-white/30 text-white uppercase text-xs font-bold tracking-[0.2em] px-5 py-3 mb-6 hover:border-primary hover:text-primary transition-colors">
        <Files className="w-4 h-4" /> Importer plusieurs liens
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="rounded-none bg-black border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
          <DialogTitle className="font-display uppercase text-2xl text-white">Importer plusieurs concerts</DialogTitle>
          <DialogDescription className="text-zinc-300 text-sm">Un lien par ligne ({MAX_LINKS} max). Chaque page est lue par l'IA, puis tu vérifies avant d'enregistrer.</DialogDescription>
          {rows.length === 0 ? (
            <>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="bulk-import-links"
                placeholder={"https://www.salle.fr/concert-1\nhttps://shotgun.live/events/…"}
                className="w-full bg-transparent border border-white/20 focus:border-white text-white placeholder:text-zinc-500 p-4 outline-none" />
              <p className="text-zinc-400 text-sm" data-testid="bulk-import-count">{parseLinks(text).length} lien(s) détecté(s)</p>
              <button type="button" onClick={run} disabled={!parseLinks(text).length} data-testid="bulk-import-run"
                className="bg-primary text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
                Lire les pages
              </button>
            </>
          ) : (
            <>
              <ul className="border-t border-white/10" data-testid="bulk-import-rows">
                {rows.map((r, i) => (
                  <Row key={r.url} row={r} i={i} onEdit={() => setEditing(i)} onToggle={() => patch(i, { selected: !r.selected })} />
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={saveSelected} disabled={busy || !selected} data-testid="bulk-import-save"
                  className="inline-flex items-center gap-2 bg-primary text-white font-display uppercase text-lg px-6 py-3 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Enregistrer la sélection ({selected})
                </button>
                <button type="button" onClick={reset} disabled={busy} data-testid="bulk-import-reset"
                  className="inline-flex items-center gap-2 border border-white/30 text-white uppercase text-xs font-bold tracking-[0.2em] px-5 hover:border-white transition-colors disabled:opacity-50">
                  <X className="w-4 h-4" /> Nouvelle liste
                </button>
              </div>
              {rows.some((r) => r.status === "duplicate") && (
                <p className="flex items-center gap-2 text-sm text-yellow-400"><AlertTriangle className="w-4 h-4" /> Les doublons ne sont pas cochés : coche-les seulement s'il s'agit d'une autre date.</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-none bg-black border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-edit-dialog">
          <DialogTitle className="font-display uppercase text-2xl text-white">Vérifier le concert</DialogTitle>
          {editing !== null && rows[editing]?.data && (
            <EventForm key={rows[editing].url} hideImport initial={toPayload(rows[editing].data)} onSubmit={saveEdited} submitting={false} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
